import { useEffect, useRef, useState, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { MobileDrawerNav, NavShell, ThemeProvider, type NavItem } from "@shop-ledger/ui-components";
import { useAuth } from "../auth/AuthContext";
import { CrossAppLinks } from "./CrossAppLinks";
import { BackToTopButton, useAutoHideOnScroll } from "./ScrollChrome";
import { ALL_NAV_ITEMS, BASIC_NAV_ITEMS, NAV_SECTIONS } from "../roles/permissions";
import { useNavMode } from "../lib/useNavMode";
import { getBranding } from "../api/brandingApi";
import type { TenantBrandingOut } from "../api/types";
import { API_ROOT_URL } from "../env";

export function NavHeader({ children }: { children: ReactNode }) {
  const { user, logout, ensureFreshToken } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [branding, setBranding] = useState<TenantBrandingOut | null>(null);
  const { mode, toggle } = useNavMode();
  // Scroll ergonomics (see ScrollChrome.tsx): the sticky mobile topbar slides away
  // scrolling down and back the moment the user scrolls up, so the hamburger never
  // requires scrolling to the top to reach.
  const topbarRef = useRef<HTMLDivElement>(null);
  useAutoHideOnScroll(topbarRef, "sl-mobile-topbar--hidden");

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    ensureFreshToken()
      .then((token) => getBranding(token))
      .then((result) => {
        if (!cancelled) setBranding(result);
      })
      .catch(() => {
        // Real GET /branding (§11.1) replaced the old MOCK_TENANT_BRANDING placeholder —
        // on any failure, ThemeProvider's own DEFAULT_THEME fallback (passing `null`) is
        // exactly as good a degrade as the old hardcoded mock was.
      });
    return () => {
      cancelled = true;
    };
  }, [user, ensureFreshToken]);

  if (!user) return <>{children}</>;

  const businessName = branding?.display_name?.trim() || "ZEL-MART";
  const logoUrl = branding?.logo_path
    ? branding.logo_path.startsWith("http")
      ? branding.logo_path
      : `${API_ROOT_URL}${branding.logo_path}`
    : null;

  const activeKey = ALL_NAV_ITEMS.find((item) => item.href === location.pathname)?.key;

  function handleNavigate(item: NavItem) {
    navigate(item.href);
  }

  // Shared between `NavShell` (desktop/tablet sidebar) and `MobileDrawerNav` (phone-width
  // drawer, below) — both take the identical `header`/`footer` slot shape, and the mobile
  // drawer is this app's *only* surface for "Advanced" toggle/sign-out/etc. once the
  // breakpoint below hides `NavShell`'s own sidebar, so the content must not be desktop-only.
  const headerContent = (
    <div style={{ minWidth: 0, display: "flex", alignItems: "center", gap: "0.5rem" }}>
      {logoUrl ? (
        <img
          src={logoUrl}
          alt=""
          style={{ height: 28, width: 28, objectFit: "contain", borderRadius: 4, flexShrink: 0 }}
        />
      ) : null}
      <div style={{ minWidth: 0 }}>
        <strong
          title={businessName}
          style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
        >
          {businessName}
        </strong>
        <div
          title={`${user.email} · ${user.role}`}
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            color: "rgba(255,255,255,0.7)",
            fontSize: "0.85rem",
          }}
        >
          {user.email} · {user.role}
        </div>
      </div>
    </div>
  );

  const footerContent = (
    <>
      <button
        type="button"
        role="switch"
        className="sl-toggle-switch"
        aria-checked={mode === "advanced"}
        onClick={toggle}
        title={mode === "advanced" ? "Hide advanced/back-office pages" : "Show advanced/back-office pages"}
      >
        <span>Advanced</span>
        <span className="sl-toggle-switch__track" aria-hidden="true">
          <span className="sl-toggle-switch__thumb" />
        </span>
      </button>
      <CrossAppLinks />
      <button type="button" className="sl-button sl-button--ghost sl-button--md" onClick={() => navigate("/account")}>
        My account
      </button>
      <button type="button" className="sl-button sl-button--ghost sl-button--md" onClick={() => void logout()}>
        Sign out
      </button>
    </>
  );

  return (
    <ThemeProvider branding={branding}>
      {/* Phone-width takeover (<=768px, see `.sl-mobile-topbar` in index.css): `NavShell`'s
          own persistent sidebar/pill-bar nav is hidden below that width and this hamburger
          top bar takes its place — the page content area is unaffected either way, only the
          navigation chrome swaps. Rendered unconditionally (not gated in JS) so its CSS
          `display: none` default never causes a layout flash while it decides. */}
      <div className="sl-mobile-topbar" ref={topbarRef}>
        <MobileDrawerNav
          role={user.role}
          items={BASIC_NAV_ITEMS}
          sections={mode === "advanced" ? NAV_SECTIONS : undefined}
          activeKey={activeKey}
          onNavigate={handleNavigate}
          header={headerContent}
          footer={footerContent}
        />
        <strong className="sl-mobile-topbar__brand" title={businessName}>
          {businessName}
        </strong>
      </div>
      <NavShell
        role={user.role}
        items={BASIC_NAV_ITEMS}
        sections={mode === "advanced" ? NAV_SECTIONS : undefined}
        activeKey={activeKey}
        onNavigate={handleNavigate}
        header={headerContent}
        footer={footerContent}
      >
        {children}
      </NavShell>
      <BackToTopButton />
    </ThemeProvider>
  );
}
